import type { RewardedAdProvider, ShowRewardedOptions } from "@yjsgames/ad-ports";
import type { AnalyticsSink } from "@yjsgames/analytics-ports";
import { AnalyticsEvent } from "../analytics/analyticsEvents.js";
import { currentCrazyGamesSdk, initCrazyGamesSdk } from "./CrazyGamesSdk.js";

/**
 * CrazyGames 웹 보상형 광고 — `requestAd("rewarded")` (DIP RewardedAdProvider 구현).
 *
 * `adFinished`에서만 보상 지급, `adError`/미사용 시 onSkip. SDK 미로드(웹 아닌 환경 등)면
 * 즉시 onSkip — crash 없이 광고 없는 환경 안전.
 *
 * 웹 빌드에서 `VITE_WEB_AD_PROVIDER=crazygames` 일 때
 * `createRewardedAdProvider`가 HouseAdProvider 대신 이 구현체를 선택한다.
 */
export class CrazyGamesRewardedProvider implements RewardedAdProvider {
  constructor(private readonly analytics: AnalyticsSink) {}

  showRewarded(opts: ShowRewardedOptions): void {
    void this.showRewardedAsync(opts);
  }

  private async showRewardedAsync(opts: ShowRewardedOptions): Promise<void> {
    const sdk = currentCrazyGamesSdk() ?? (await initCrazyGamesSdk(true));
    if (!sdk) {
      this.analytics.log(AnalyticsEvent.RewardedAdError, { provider: "crazygames" });
      opts.onSkip?.();
      return;
    }
    let settled = false;
    const finishWithSkip = (): void => {
      if (settled) return;
      settled = true;
      this.analytics.log(AnalyticsEvent.RewardedAdError, { provider: "crazygames" });
      opts.onSkip?.();
    };
    try {
      sdk.ad.requestAd("rewarded", {
        adFinished: () => {
          if (settled) return;
          settled = true;
          this.analytics.log(AnalyticsEvent.RewardedAdComplete, { provider: "crazygames" });
          opts.onReward();
        },
        adError: finishWithSkip,
      });
    } catch (error) {
      console.warn("[CrazyGames] rewarded request failed", error);
      finishWithSkip();
    }
  }
}
